import { notifyOnError } from '~/rage/decorators';
import { BackendService } from '../BackendService';
import type { RpcService } from '../RpcService';
import { IAccountsService } from './accountsServiceTypes';

export class AccountsService implements IAccountsService {
	constructor(
		private readonly rpc: RpcService,
		private readonly backend: BackendService,
	) {}

	@notifyOnError
	public async login(
		emailOrLogin: string,
		password: string,
		remember = false,
	): Promise<void> {
		await this.rpc.invokeServer<string>('accounts:login', {
			emailOrLogin,
			password,
			remember,
		});
	}

	@notifyOnError
	public async restorePassword(
		email: string,
		login: string,
	): Promise<{ resendAvailableAt: number; maskedEmail: string }> {
		return this.backend.invoke<{
			resendAvailableAt: number;
			maskedEmail: string;
		}>('accounts:requestPasswordReset', {
			email,
			login,
		});
	}

	@notifyOnError
	public async confirmResetCode(
		email: string,
		code: string,
	): Promise<{ token: string; expiresAt: number }> {
		return this.backend.invoke<{ token: string; expiresAt: number }>(
			'accounts:confirmPasswordResetCode',
			{
				email,
				code,
			},
		);
	}

	@notifyOnError
	public async resetPassword(
		email: string,
		token: string,
		newPassword: string,
	): Promise<void> {
		await this.backend.invoke<void>('accounts:resetPassword', {
			email,
			token,
			password: newPassword,
		});
	}

	@notifyOnError
	public async sendRegistrationCode(
		email: string,
		login: string,
		password: string,
	): Promise<{ resendAvailableAt: number; maskedEmail: string }> {
		return this.backend.invoke<{
			resendAvailableAt: number;
			maskedEmail: string;
		}>('accounts:sendRegistrationCode', { email, login, password });
	}

	@notifyOnError
	public async confirmRegistrationCode(
		email: string,
		code: string,
	): Promise<{
		createdAt: number;
		accountId: string;
		resetToken?: string;
		expiresAt?: number;
	}> {
		return this.backend.invoke<{
			createdAt: number;
			accountId: string;
			resetToken?: string;
			expiresAt?: number;
		}>('accounts:confirmRegistrationCode', { email, code });
	}

	public async verifyServerPassword(password: string): Promise<void> {
		await this.rpc.invokeServer('auth:verifyServerPassword', { password });
	}

	@notifyOnError
	public async verifyClosedBetaCode(code: string): Promise<void> {
		await this.rpc.invokeServer('auth:verifyClosedBetaCode', { code });
	}
}
